import React, { createContext, useState, useContext } from "react";
import { AuthContext } from "State/AuthContext";
import { useNavigate } from "react-router-dom";
import { loginUser, registerTempUser, createNewUser, forgotPassword, reSendOtpSignUp, reSendOtpForgotPassword, verifyOtpWithInvitationID, verifyOtpWithEMail, resetPassword } from "../Api/apiService";

export const AuthFunctionsContext = createContext();

export const AuthFunctionsProvider = ({ children }) => {
    const { setEmail, setAccessToken, setRefreshToken, setProfileName, setProfileRole, setOrgDomain, setSubscriptionType, errorMessageData } = useContext(AuthContext);

    // States for the form
    const [username, setUsername] = useState("");
    const [password, setPassword] = useState("");
    const [firstName, setFirstName] = useState("");
    const [lastName, setLastName] = useState("");
    const [companyName, setCompanyName] = useState("");
    const [jobTitle, setJobTitle] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [isSignup, setIsSignup] = useState(false); // Track whether it's signup or login
    const [isForgotPassword, setIsForgotPassword] = useState(false); // Track forgot password view
    const [invitationId, setInvitationId] = useState(""); // Store invitation_id returned from the signup API
    const [otp, setOtp] = useState("");
    const [hashedValue, setHashedValue] = useState("");
    const [signupStep, setSignupStep] = useState(1); // Step 1: Email/Name, Step 2: OTP/Password
    const [forgotPasswordStep, setForgotPasswordStep] = useState(1); // 1 for email input, 2 for OTP and new password
    const [authStatus, setAuthStatus] = useState(""); // 1 for email input, 2 for OTP and new password
    const [otpValid, setOtpValid] = useState(""); // 1 for email input, 2 for OTP and new password
    const [captchaToken, setCaptchaToken] = useState(""); // Store CAPTCHA token


    function getErrorMessageForUI(errorCode) {
        const error = errorMessageData.find(item => item.error_code === errorCode);
        return error ? error.error_message_for_ui : "Unknown error. Please try again.";
    }

    const navigate = useNavigate();

    const [error, setError] = useState({
        username: "",
        password: "",
        confirmPassword: "",
        firstName: "",
        lastName: "",
        authfail: "",
        companyName: "",
        jobTitle: "",
        captcha: ""
    });
    const [loadingAuth, setLoadingAuth] = useState(false);

    const sb_auth_validate_email = (email) => {
        const regex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
        return regex.test(email);
    };

    const sb_auth_validate_password = (password) => {
        const regex = /^(?=.*[A-Z])(?=.*[a-z])(?=.*\d)(?=.*[~!@#$%^&*])[A-Za-z\d~!@#$%^&*]{8,12}$/;
        return regex.test(password);
    };

    const sb_auth_handleBlur = (e) => {
        const { id, value } = e.target;

        if (id === "email") {
            if (!value) {
                setError((prevState) => ({ ...prevState, username: "Enter a business email address." }));
            } else if (!sb_auth_validate_email(value)) {
                setError((prevState) => ({ ...prevState, username: "Enter a valid business email address." }));
            } else {
                setError((prevState) => ({ ...prevState, username: "" }));
            }
        }

        if (id === "password") {
            if (!value) {
                setError((prevState) => ({ ...prevState, password: "Enter password. Password must have between 8 and 12 characters, with at least one capital letter, one small letter, one number and one of these nine special characters ~!@#$%^&*" }));
            } else if (!sb_auth_validate_password(value)) {
                setError((prevState) => ({ ...prevState, password: "Password must have between 8 and 12 characters, with at least one capital letter, one small letter, one number and one of these nine special characters ~!@#$%^&*" }));
            } else {
                setError((prevState) => ({ ...prevState, password: "" }));
            }
        }

        if (id === "firstName") {
            if (!value) {
                setError((prevState) => ({ ...prevState, firstName: "Enter first name." }));
            } else {
                setError((prevState) => ({ ...prevState, firstName: "" }));
            }
        }

        if (id === "lastName") {
            if (!value) {
                setError((prevState) => ({ ...prevState, lastName: "Enter last name." }));
            } else {
                setError((prevState) => ({ ...prevState, lastName: "" }));
            }
        }

        if (id === "companyName") {
            if (!value) {
                setError((prevState) => ({ ...prevState, companyName: "Enter company name." }));
            } else {
                setError((prevState) => ({ ...prevState, companyName: "" }));
            }
        }

        if (id === "jobTitle") {
            if (!value) {
                setError((prevState) => ({ ...prevState, jobTitle: "Enter job title." }));
            } else {
                setError((prevState) => ({ ...prevState, jobTitle: "" }));
            }
        }

        if (id === "confirmPassword") {
            if (value !== password) {
                setError((prevState) => ({ ...prevState, confirmPassword: "Passwords do not match." }));
            } else {
                setError((prevState) => ({ ...prevState, confirmPassword: "" }));
            }
        }

        if (id === "otp") {
            if (!value) {
                setError((prevState) => ({ ...prevState, otp: "Enter OTP." }));
            } else {
                setError((prevState) => ({ ...prevState, otp: "" }));
            }
        }
    };

    const sb_auth_handleLogin = async (e, recaptchaRef) => {
        e?.preventDefault?.();
        setError({}); // Clear previous errors
        setAuthStatus("");

        // Basic validation for login
        if (!username || !password || error.username || error.password) {
            return; // Don't submit if any validation error exists
        }

        // Validate CAPTCHA
        if (!captchaToken) {
            setError({ captcha: "Please complete the CAPTCHA verification" });
            return;
        }

        setLoadingAuth(true); // Start loading

        try {
            const loginResponse = await loginUser(username, password, captchaToken); // Call login API

            if (loginResponse?.access_token) {
                localStorage.setItem("access_token", loginResponse.access_token);
                setAccessToken(loginResponse.access_token);
            }

            if (loginResponse?.refresh_token) {
                localStorage.setItem("refresh_token", loginResponse.refresh_token);
                setRefreshToken(loginResponse.refresh_token);
            }

            if (loginResponse?.name) {
                localStorage.setItem("name", loginResponse.name);
                setProfileName(loginResponse.name);
            }

            if (loginResponse?.domain) {
                localStorage.setItem("domain", loginResponse.domain);
                setOrgDomain(loginResponse.domain);
            }

            if (loginResponse?.subscription_type) {
                localStorage.setItem("subscription_type", loginResponse.subscription_type);
                setSubscriptionType(loginResponse.subscription_type);
            }

            if (loginResponse?.auth_type) {
                localStorage.setItem("auth_type", loginResponse.auth_type);
            }

            if (loginResponse?.sso_idp_login_url) {
                localStorage.setItem("sso_idp_login_url", loginResponse.sso_idp_login_url);
            }
            localStorage.setItem("username", username);
            setEmail(username);
            navigate("/jobs-library");

        } catch (err) {
            console.error("Login unsuccessful:", err);

            if (err.response.data.error_code) {
                let errorMessage = getErrorMessageForUI(err.response.data.error_code);
                setError({ authfail: errorMessage });
            } else {
                setError({ authfail: "Enter valid business email and password." });
            }
            
            // Reset CAPTCHA on failed login attempt
            if (recaptchaRef && recaptchaRef.current) {
                recaptchaRef.current.reset();
                setCaptchaToken("");
            }

        } finally {
            setLoadingAuth(false); // Stop loading after the request completes
        }
    };

    const sb_auth_handleSignupStep1 = async (e) => {
        e?.preventDefault?.();
        setError({}); // Clear previous errors

        const cleanedFirstName = firstName.trim();
        const cleanedLastName = lastName.trim();
        const cleanedCompanyName = companyName.trim();
        const cleanedJobTitle = jobTitle.trim();

        // Basic validation for step 1
        if (!username || !cleanedFirstName || !cleanedLastName || !cleanedCompanyName || !cleanedJobTitle || error.username) {
            return; // Don't submit if any validation error exists
        }

        setLoadingAuth(true); // Start loading

        try {
            // Send OTP API Call
            const signUpResponse1 = await registerTempUser(cleanedFirstName, cleanedLastName, username, cleanedCompanyName, cleanedJobTitle);
            if (signUpResponse1?.invitation_id) {
                setInvitationId(signUpResponse1.invitation_id);
                setSignupStep(2);
            }

        } catch (err) {

            console.error("Signup unsuccessful at step 1:", err);

            if (err.response.data.error_code) {
                let errorMessage = getErrorMessageForUI(err.response.data.error_code);
                setError({ authfail: errorMessage });
            } else {
                setError({ authfail: "There seems to be a technical problem. Please try again in a few minutes. If this persists, please contact us at info@skillsbridge.ai" });
            }

        } finally {
            setLoadingAuth(false); // Stop loading after the request completes
        }
    };

    // Signup function for step 2 (after OTP is sent)
    const sb_auth_handleSignupStep2 = async (e) => {
        e?.preventDefault?.();
        setError({}); // Clear previous errors

        // Basic validation for step 2
        if (!otp || error.otp) {
            return; // Don't submit if any validation error exists
        }

        setLoadingAuth(true); // Start loading

        try {
            // Step 2 API call for otp validation
            const signUpResponse2 = await verifyOtpWithInvitationID(invitationId, otp);
            if (signUpResponse2?.otp_verification_code) {
                setHashedValue(signUpResponse2.otp_verification_code)
                setSignupStep(3);
            }

        } catch (err) {
            console.error("Signup unsuccessful at step 2:", err);


            if (err.response.data.error_code) {
                let errorMessage = getErrorMessageForUI(err.response.data.error_code);
                setError({ authfail: errorMessage });
            } else {
                setError({ authfail: "There seems to be a technical problem. Please try again in a few minutes. If this persists, please contact us at info@skillsbridge.ai" });
            }

        } finally {
            setLoadingAuth(false); // Stop loading after the request completes
        }
    };


    // Signup function for step 2 (after OTP is sent)
    const sb_auth_handleSignupStep3 = async (e) => {
        e?.preventDefault?.();
        setError({}); // Clear previous errors


        if (password !== confirmPassword) {
            setError({ confirmPassword: "Passwords do not match." });
            setLoadingAuth(false);
            return;
        }

        // Basic validation for step 3
        if (!password || !confirmPassword || password !== confirmPassword || error.password || error.confirmPassword) {
            return; // Don't submit if any validation error exists
        }

        setLoadingAuth(true); // Start loading

        try {
            // Step 3 API call for signup 
            const signUpResponse3 = await createNewUser(hashedValue, password);
            if (signUpResponse3?.message) {
                setAuthStatus(signUpResponse3.message)
                setIsSignup(false);
            }

        } catch (err) {
            console.error("Signup unsuccessful at step 3:", err);

            if (err.response.data.error_code) {
                let errorMessage = getErrorMessageForUI(err.response.data.error_code);
                setError({ authfail: errorMessage });
            } else {
                setError({ authfail: "There seems to be a technical problem. Please try again in a few minutes. If this persists, please contact us at info@skillsbridge.ai" });
            }

        } finally {
            setLoadingAuth(false);
        }
    };


    // Function to re send OTP for sign up
    const sb_auth_reSendOtpSignup = async () => {
        setLoadingAuth(true);
        setError({}); // Clear previous errors

        try {
            const reSendOtpSignupResponse1 = await reSendOtpSignUp(invitationId);
            if (reSendOtpSignupResponse1?.message) {

                if (reSendOtpSignupResponse1.message === "OTP already sent which is still valid, please check your email.") {
                    setOtpValid("Use OTP already sent by email.")
                }
            }
            setLoadingAuth(false);

        } catch (err) {
            console.error("Error resending OTP in signup:", err);

            if (err.response.data.error_code) {
                let errorMessage = getErrorMessageForUI(err.response.data.error_code);
                setError({ authfail: errorMessage });
            } else {
                setError({ authfail: "There seems to be a technical problem. Please try again in a few minutes. If this persists, please contact us at info@skillsbridge.ai" });
            }

            setLoadingAuth(false);

        }
    };

    // Function to send OTP for forgot password
    const sb_auth_forgotPasswordStep1 = async (username, recaptchaRef) => {
        setLoadingAuth(true);
        setError({}); // Clear previous errors

        // Validate CAPTCHA
        if (!captchaToken) {
            setError({ captcha: "Please complete the CAPTCHA verification" });
            setLoadingAuth(false);
            return;
        }

        try {
            const forgotPasswordResponse1 = await forgotPassword(username, captchaToken);
            if (forgotPasswordResponse1?.message) {

                if (forgotPasswordResponse1.message === "OTP already sent which is still valid, please check your email.") {
                    setOtpValid("Use OTP already sent by email.")
                }
            }

            setLoadingAuth(false);
            setForgotPasswordStep(2);


        } catch (err) {
            console.error("Error sending OTP for forgot password:", err);
            
            // Reset CAPTCHA on failed attempt
            if (recaptchaRef && recaptchaRef.current) {
                recaptchaRef.current.reset();
                setCaptchaToken("");
            }
            
            setLoadingAuth(false);
            setForgotPasswordStep(2);

        }
    };

    // Function to verify OTP
    const sb_auth_forgotPasswordStep2 = async (username, otp) => {
        setLoadingAuth(true);
        setError({}); // Clear previous errors

        try {
            const forgotPasswordResponse2 = await verifyOtpWithEMail(username, otp);
            if (forgotPasswordResponse2?.otp_verification_code) {
                setHashedValue(forgotPasswordResponse2.otp_verification_code)
                setLoadingAuth(false);
                setForgotPasswordStep(3);
            }
        } catch (err) {
            console.error("Error Validating OTP:", err);

            if (err.response.data.error_code) {
                let errorMessage = getErrorMessageForUI(err.response.data.error_code);
                setError({ authfail: errorMessage });
            } else {
                setError({ authfail: "There seems to be a technical problem. Please try again in a few minutes. If this persists, please contact us at info@skillsbridge.ai" });
            }

            setLoadingAuth(false);
        }
    };

    // Function to reset password
    const sb_auth_forgotPasswordStep3 = async (username, newPassword, confirmNewPassword) => {
        setLoadingAuth(true);
        setError({}); // Clear previous errors

        if (newPassword !== confirmNewPassword) {
            setError({ confirmPassword: "Passwords do not match." });
            setLoadingAuth(false);
            return;
        }

        try {
            const forgotPasswordResponse2 = await resetPassword(username, hashedValue, newPassword);
            if (forgotPasswordResponse2?.message) {
                setAuthStatus(forgotPasswordResponse2?.message);
                setLoadingAuth(false);
                setIsForgotPassword(false);
            }
        } catch (err) {
            console.error("Error resetting password:", err);

            if (err.response.data.error_code) {
                let errorMessage = getErrorMessageForUI(err.response.data.error_code);
                setError({ authfail: errorMessage });
            } else {
                setError({ authfail: "There seems to be a technical problem. Please try again in a few minutes. If this persists, please contact us at info@skillsbridge.ai" });
            }

            setLoadingAuth(false);
        }
    };

    // function to resend otp for forgot password

    const sb_auth_reSendOtpForgot = async (username) => {
        setLoadingAuth(true);
        setError({}); // Clear previous errors

        try {
            const resendOtpForgotResponse1 = await reSendOtpForgotPassword(username);
            if (resendOtpForgotResponse1?.message) {

                if (resendOtpForgotResponse1.message === "OTP already sent which is still valid, please check your email.") {
                    setOtpValid("Use OTP already sent by email.")
                }
            }

            setLoadingAuth(false);

        } catch (err) {
            console.error("Error resending OTP in forgot password:", err);

            if (err.response.data.error_code) {
                let errorMessage = getErrorMessageForUI(err.response.data.error_code);
                setError({ authfail: errorMessage });
            } else {
                setError({ authfail: "There seems to be a technical problem. Please try again in a few minutes. If this persists, please contact us at info@skillsbridge.ai" });
            }

            setLoadingAuth(false);
        }
    };

    const toggleSignup = () => setIsSignup((prev) => !prev, setError({}), setSignupStep(1), setAuthStatus(""));
    const toggleForgotPassword = () => setIsForgotPassword((prev) => !prev, setError({}), setForgotPasswordStep(1), setAuthStatus(""));
    return (
        <AuthFunctionsContext.Provider
            value={{
                username,
                setUsername,
                password,
                setPassword,
                firstName,
                setFirstName,
                lastName,
                setLastName,
                confirmPassword,
                setConfirmPassword,
                error,
                setError,
                loadingAuth,
                sb_auth_handleLogin,
                sb_auth_handleSignupStep1,
                sb_auth_handleSignupStep2,
                sb_auth_handleSignupStep3,
                sb_auth_handleBlur,
                sb_auth_forgotPasswordStep1,
                sb_auth_forgotPasswordStep2,
                sb_auth_forgotPasswordStep3,
                isSignup,
                toggleSignup,
                isForgotPassword,
                toggleForgotPassword,
                otp,
                setOtp,
                signupStep,
                setSignupStep,
                forgotPasswordStep,
                setForgotPasswordStep,
                authStatus,
                setAuthStatus,
                companyName,
                setCompanyName,
                jobTitle,
                setJobTitle,
                otpValid,
                setOtpValid,
                sb_auth_reSendOtpSignup,
                sb_auth_reSendOtpForgot,
                captchaToken,
                setCaptchaToken,
            }}
        >
            {children}
        </AuthFunctionsContext.Provider>
    );
};
